/**
 * Badge Component — Soorgaa Design System
 * 
 * Micro status indicator and label chip.
 * Uses rounded-xs (2px) or rounded-full per architectural rules, text-xs minimum.
 */
import React from "react";

export type BadgeVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "success"
  | "warning"
  | "destructive"
  | "info";

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  pill?: boolean;
  children: React.ReactNode;
  className?: string;
}

export function Badge({
  variant = "secondary",
  pill = false,
  children,
  className = "",
  ...props
}: BadgeProps) {
  const variantClasses: Record<BadgeVariant, string> = {
    primary: "bg-primary/10 text-primary border-primary/20",
    secondary: "bg-secondary text-secondary-foreground border-border",
    outline: "bg-transparent text-foreground border-border",
    success: "bg-success/10 text-success border-success/30",
    warning: "bg-warning/10 text-warning border-warning/30",
    destructive: "bg-destructive/10 text-destructive border-destructive/30",
    info: "bg-info/10 text-info border-info/30",
  };

  const radiusClass = pill ? "rounded-full" : "rounded-xs";

  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 border ${radiusClass} ${variantClasses[variant]} ${className}`.trim()}
      {...props}
    >
      {children}
    </span>
  );
}

export { SubjectBadge, StatusBadge } from "./Badges";
